import { useAuth0 } from '@auth0/auth0-react'
import { useFlag } from '@unleash/proxy-client-react'
import { useEffect, useRef } from 'react'
import { useGetForethoughtToken } from 'src/api/forethought/useGetForethoughtToken'
import { FF_ENABLE_FORETHOUGHT_CONTEXT_VARIABLES } from 'src/constants/feature-flags'
import { useCancelSubscription } from 'src/hooks/useCancelSubscription'

if (!process.env.REACT_APP_FORETHOUGHT_API_KEY) {
  throw new Error('REACT_APP_FORETHOUGHT_API_KEY is not set')
}

const FORETHOUGHT_WIDGET_ID = 'forethought-chat'
const FORETHOUGHT_SCRIPT_ID = 'forethought-widget-embed-script'

const attributes = {
  src: 'https://solve-widget.forethought.ai/embed.js',
  id: 'forethought-widget-embed-script',
  type: 'application/javascript',
  'data-api-key': process.env.REACT_APP_FORETHOUGHT_API_KEY,
  'data-ft-Embed-Script-Language': '',
  'data-ft-workflow-tag': 'Live',
  position: 'left'
}

const INVALID_BRIGHTBACK_URL = 'invalid-brightback-url'
const INVALID_TOKEN = 'invalid-token'

const MAX_BRIGHTBACK_RETRIES = 3

export function openChatbot() {
  if (!window.Forethought) {
    return
  }

  window.Forethought('widget', 'open')
}

function addScriptToHead({
  brightbackUrl,
  token
}: {
  brightbackUrl: string
  token: string
}) {
  const script = document.createElement('script')

  Object.entries(attributes).forEach(([key, value]) => {
    script.setAttribute(key, value)
  })

  script.setAttribute('data-ft-brightback-url', brightbackUrl)
  script.setAttribute('data-ft-token', token)

  document.querySelector(FORETHOUGHT_WIDGET_ID)?.remove()
  document.head.append(script)
}

export const Forethought = () => {
  const { isAuthenticated } = useAuth0()
  const { isLoading, token, error } = useGetForethoughtToken()
  const { getBrightbackUrl } = useCancelSubscription()
  const isForethoughtContextVariablesEnabled = useFlag(
    FF_ENABLE_FORETHOUGHT_CONTEXT_VARIABLES
  )

  const brightbackRetries = useRef(0)

  useEffect(() => {
    // If the script is already set, don't do anything
    const existingScript = document.getElementById(FORETHOUGHT_SCRIPT_ID)
    if (existingScript) {
      return
    }

    /**
     * If the ff is disabled, set the brightback URL and token to an invalid value, and it's handled directly in Forethought
     * (btw, these workflows are probably also disabled there, so it's not an issue)
     */
    if (!isForethoughtContextVariablesEnabled || error) {
      addScriptToHead({
        brightbackUrl: INVALID_BRIGHTBACK_URL,
        token: INVALID_TOKEN
      })

      return
    }

    // Avoid setting the script if the user is not authenticated, the token is loading
    if (!isAuthenticated || isLoading) {
      return
    }

    getBrightbackUrl().then(brightbackUrl => {
      // If we couldn't get the brightback URL, try again up to 3 times
      if (
        !brightbackUrl &&
        brightbackRetries.current < MAX_BRIGHTBACK_RETRIES
      ) {
        brightbackRetries.current++
        return
      }

      addScriptToHead({
        brightbackUrl: brightbackUrl || INVALID_BRIGHTBACK_URL,
        token: token || INVALID_TOKEN
      })
    })
  }, [
    isAuthenticated,
    token,
    isLoading,
    isForethoughtContextVariablesEnabled,
    error,
    getBrightbackUrl
  ])

  return null
}
